import { useState, useEffect } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import { DEFAULT_SETUP_PRICING, normaliseSetupPricing, type SetupPricing, type PlanContent } from '../data/setupPricing';
import AdminLayout from '../components/AdminLayout';
import AccountingPackagesEditor, { DEFAULT_PACKAGES, type Pkg } from '../components/AccountingPackagesEditor';
import SetupPlanEditor from '../components/SetupPlanEditor';
import Dh from '../components/Dh';
import { Save, CheckCircle2, AlertTriangle, Info, Tag } from 'lucide-react';

/* Package Pricing — two tabs over two independent Firestore docs:

   Accounting tab → `pricing/current.packages` (the /accounting/pricing plan cards).
     Saved with a MERGE write of just { packages }, so the fees and bands that share
     that doc — and are edited on /admin/pricing — are preserved.
     The same editor is mirrored on /admin/pricing; both target this field, so
     whichever page you use, the other reflects it.

   Corporate tab → `setupPricing/current` (company-setup + operate flow pricing).
     Drives the plan cards (SetupPlan/OperatePlan) and the invoice totals
     (SetupInvoice/OperateInvoice). Unchanged from before the tabs were added.

   Each tab saves its own doc, so the two save buttons are deliberately separate —
   one button writing both docs would make a partial failure ambiguous. */

type Tab = 'accounting' | 'corporate';

const clone = (m: SetupPricing): SetupPricing => JSON.parse(JSON.stringify(m));
const inputCls = 'w-full border-2 border-gray-200 rounded-lg pl-7 pr-3 py-2 text-sm font-bold bg-[var(--brand-soft)] text-gray-900 focus:outline-none focus:border-[var(--brand)]';
const lbl = 'text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-1';

const TABS: { key: Tab; label: string; sub: string }[] = [
  { key: 'accounting', label: 'Accounting', sub: 'Subscription plans on the public /accounting/pricing page' },
  { key: 'corporate', label: 'Corporate', sub: 'Company-formation & operate plan prices + fees, live on the setup/operate flows instantly' },
];

function AedInput({ value, onChange, allowNull = false }: { value: number | null; onChange: (v: number | null) => void; allowNull?: boolean }) {
  return (
    <div className="relative flex items-center">
      <span className="absolute left-3 pointer-events-none text-gray-400"><Dh em="0.75" style={{ marginRight: 0 }} /></span>
      <input type="number" min={0} value={value ?? ''} placeholder={allowNull ? '-' : '0'}
        onChange={e => onChange(e.target.value === '' ? (allowNull ? null : 0) : Math.max(0, +e.target.value || 0))}
        className={inputCls} />
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-[var(--brand)] to-[var(--brand-d)]" />
      <div className="p-5">
        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">{title}</div>
        {children}
      </div>
    </div>
  );
}

export default function AdminSetupPricing() {
  const [tab, setTab] = useState<Tab>('accounting');
  const [loading, setLoading] = useState(true);

  // Corporate tab — setupPricing/current
  const [m, setM] = useState<SetupPricing>(DEFAULT_SETUP_PRICING);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Accounting tab — pricing/current.packages
  const [packages, setPackages] = useState<Pkg[]>(DEFAULT_PACKAGES);
  const [pkgSaving, setPkgSaving] = useState(false);
  const [pkgSavedAt, setPkgSavedAt] = useState<string | null>(null);
  const [pkgLoadError, setPkgLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadCorporate = async () => {
      try {
        const snap = await getDoc(doc(db, 'setupPricing', 'current'));
        // Same normalisation the public flows use, so the editor can never show a
        // shape the flows would interpret differently.
        if (snap.exists()) setM(normaliseSetupPricing(snap.data()));
      } catch (e: any) {
        console.error('Failed to load setupPricing/current:', e);
        if (e?.code === 'permission-denied') setLoadError('Firestore permission denied: showing defaults. Sign in as a Firebase-authenticated admin to save.');
      }
    };

    const loadAccounting = async () => {
      try {
        const snap = await getDoc(doc(db, 'pricing', 'current'));
        if (snap.exists()) {
          const d = snap.data();
          if (Array.isArray(d.packages) && d.packages.length) setPackages(d.packages);
        }
      } catch (e: any) {
        console.error('Failed to load pricing/current:', e);
        if (e?.code === 'permission-denied') setPkgLoadError('Firestore permission denied: showing default plans. Sign in as a Firebase-authenticated admin to save.');
      }
    };

    (async () => {
      await Promise.all([loadCorporate(), loadAccounting()]);
      setLoading(false);
    })();
  }, []);

  const patch = (fn: (d: SetupPricing) => void) => { setM(prev => { const d = clone(prev); fn(d); return d; }); setSavedAt(null); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAdminConfig('setupPricing/current', { ...m });
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'setupPricing/current');
    } catch (e: any) {
      console.error('Save failed:', e);
      showAlert(e?.code === 'permission-denied' ? 'Save failed. Permission denied. This editor needs a Firebase-authenticated admin session.' : 'Save failed. Check console.');
    } finally { setSaving(false); }
  };
  const handleReset = () => { setM(clone(DEFAULT_SETUP_PRICING)); setSavedAt(null); };

  // Merge write — only `packages` is sent so the fees/bands in the same doc survive.
  const handleSavePackages = async () => {
    setPkgSaving(true);
    try {
      await saveAdminConfig('pricing/current', { packages });
      setPkgSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'pricing/current', { keys: ['packages'] });
    } catch (e: any) {
      console.error('Save failed:', e);
      showAlert(e?.code === 'permission-denied' ? 'Save failed. Permission denied. This editor needs a Firebase-authenticated admin session.' : 'Save failed. Check console.');
    } finally { setPkgSaving(false); }
  };
  const handleResetPackages = () => { setPackages(DEFAULT_PACKAGES); setPkgSavedAt(null); };
  const updatePackages = (next: Pkg[]) => { setPackages(next); setPkgSavedAt(null); };

  if (loading) return <AdminLayout section="Pricing" title="Package Pricing" icon={Tag} loading loadingLabel="Loading package pricing…" />;

  const activeSub = TABS.find(t => t.key === tab)?.sub ?? '';

  // One price row per plan id actually configured, in flow order. Prices are shared
  // across both flows, so a plan appearing in each is priced once. Stored prices whose
  // plan is no longer on either flow are still listed (flagged) rather than hidden.
  const priceRows = (() => {
    const rows = new Map<string, { label: string; orphan: boolean }>();
    for (const p of [...m.setupPlans, ...m.operatePlans]) {
      if (!rows.has(p.id)) rows.set(p.id, { label: p.title || p.id, orphan: false });
    }
    for (const id of Object.keys(m.plans)) {
      if (!rows.has(id)) rows.set(id, { label: id, orphan: true });
    }
    return Array.from(rows, ([id, v]) => ({ id, ...v }));
  })();

  const setPlanList = (key: 'setupPlans' | 'operatePlans') => (next: PlanContent[]) =>
    patch(d => { d[key] = next; });

  return (
    <AdminLayout
      section="Pricing"
      title="Package Pricing"
      icon={Tag}
      subtitle={activeSub}
      spaced
      actions={
        <div className="tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              aria-current={tab === t.key ? 'page' : undefined}
              className={`tab${tab === t.key ? ' active' : ''}`}
            >
          {t.label}
        </button>
      ))}
    </div>
  }
>
      {tab === 'accounting' ? (
        <>
          {pkgLoadError && (
            <div className="flex gap-2 items-start bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-xs font-bold text-red-800">
              <AlertTriangle className="w-4 h-4 flex-none stroke-red-500 mt-0.5" /><span>{pkgLoadError}</span>
            </div>
          )}
          <div className="flex gap-2 items-start bg-[var(--brand-soft)] border border-[var(--brand-line)] rounded-xl px-4 py-3 text-xs font-bold text-[var(--brand-d)]">
            <Info className="w-4 h-4 flex-none stroke-[var(--brand)] mt-0.5" />
            <span>These are the same plans you can edit under <b>Accounting Pricing</b>: both write to the same record, so a change here shows up there too. Saving this tab does not touch the VAT/CT fees or bookkeeping bands.</span>
          </div>

          <AccountingPackagesEditor packages={packages} onChange={updatePackages} />

          <div className="flex items-center gap-3 flex-wrap pb-8">
            <button onClick={handleSavePackages} disabled={pkgSaving} className="flex items-center gap-2 bg-[var(--brand)] hover:bg-[var(--brand-h)] disabled:opacity-60 text-[var(--brand-on)] font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md transition-all hover:-translate-y-0.5 active:translate-y-0">
              <Save className="w-4 h-4" />{pkgSaving ? 'Saving…' : 'Save accounting packages'}
            </button>
            <button onClick={handleResetPackages} className="border border-gray-200 bg-white text-gray-600 font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">Reset to defaults</button>
            {pkgSavedAt && <span className="flex items-center gap-1.5 text-green-600 font-bold text-xs"><CheckCircle2 className="w-4 h-4" /> Saved · live as of {pkgSavedAt}</span>}
          </div>
        </>
      ) : (
        <>
          {loadError && (
            <div className="flex gap-2 items-start bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-xs font-bold text-red-800">
              <AlertTriangle className="w-4 h-4 flex-none stroke-red-500 mt-0.5" /><span>{loadError}</span>
            </div>
          )}
          <div className="flex gap-2 items-start bg-[var(--brand-soft)] border border-[var(--brand-line)] rounded-xl px-4 py-3 text-xs font-bold text-[var(--brand-d)]">
            <Info className="w-4 h-4 flex-none stroke-[var(--brand)] mt-0.5" />
            <span>Everything the incorporate &amp; operate onboarding shows (card titles, copy, features, order and prices) plus the invoice totals. The two flows have separate cards because they carry different copy, but a plan is priced once. Plans marked with a padlock can be renamed and reordered but not removed: the signup flow keys off their id.</span>
          </div>

          <Card title="Plan prices (AED)">
            <p className="text-[11px] text-gray-400 font-medium -mt-2 mb-4">One price per plan, shared by both flows. Leave the monthly price empty for annual-only plans.</p>
            <div className="grid grid-cols-[1fr_150px_150px] gap-x-4 gap-y-3 items-center">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Plan</div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Per year</div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Per month</div>
              {priceRows.map(({ id, label, orphan }) => (
                <div key={id} className="contents">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm text-gray-900 truncate">{label}</div>
                    <div className="text-[10px] font-mono text-gray-400">{id}{orphan ? ' · not on either flow' : ''}</div>
                  </div>
                  <AedInput value={m.plans[id]?.priceYr ?? 0} onChange={v => patch(d => { d.plans[id] = { ...(d.plans[id] || { priceYr: 0, priceMo: null }), priceYr: v ?? 0 }; })} />
                  <AedInput allowNull value={m.plans[id]?.priceMo ?? null} onChange={v => patch(d => { d.plans[id] = { ...(d.plans[id] || { priceYr: 0, priceMo: null }), priceMo: v }; })} />
                </div>
              ))}
            </div>
          </Card>

          <SetupPlanEditor
            title="Incorporate flow cards"
            hint="The plan cards on /setup-plan, for founders registering a new company. Order here is the order shown."
            plans={m.setupPlans}
            onChange={setPlanList('setupPlans')}
            reservedIds={m.operatePlans.map(p => p.id)}
          />

          <SetupPlanEditor
            title="Operate flow cards"
            hint="The plan cards on /operate-plan, for founders who already have a registered company. Order here is the order shown."
            plans={m.operatePlans}
            onChange={setPlanList('operatePlans')}
            reservedIds={m.setupPlans.map(p => p.id)}
          />

          <Card title="Fees">
            <label className="flex items-start gap-3 mb-4 cursor-pointer select-none">
              <input type="checkbox" checked={m.govFeePCR} onChange={e => patch(d => { d.govFeePCR = e.target.checked; })}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-[var(--brand)]" />
              <span className="text-sm text-gray-700">
                <b>Show government fee as “PCR” (Per Client Request)</b>
                <span className="block text-xs text-gray-500 mt-0.5">Phase 1 default. When on, the invoices show “PCR” and the gov fee is excluded from the total (clients are charged the package fee only). Turn off once government fees are finalised to charge the fixed figure below.</span>
              </span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><label className={lbl}>Government fee (one-time){m.govFeePCR ? ' · inactive while PCR' : ''}</label><AedInput value={m.govFee} onChange={v => patch(d => { d.govFee = v ?? 0; })} /></div>
              <div><label className={lbl}>Expedited formation fee</label><AedInput value={m.expeditedFee} onChange={v => patch(d => { d.expeditedFee = v ?? 0; })} /></div>
              <div><label className={lbl}>Total Compliance upgrade</label><AedInput value={m.upgradePrice} onChange={v => patch(d => { d.upgradePrice = v ?? 0; })} /></div>
            </div>
          </Card>

          <div className="flex items-center gap-3 flex-wrap pb-8">
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[var(--brand)] hover:bg-[var(--brand-h)] disabled:opacity-60 text-[var(--brand-on)] font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md transition-all hover:-translate-y-0.5 active:translate-y-0">
              <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save setup pricing'}
            </button>
            <button onClick={handleReset} className="border border-gray-200 bg-white text-gray-600 font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">Reset to defaults</button>
            {savedAt && <span className="flex items-center gap-1.5 text-green-600 font-bold text-xs"><CheckCircle2 className="w-4 h-4" /> Saved · live as of {savedAt}</span>}
          </div>
        </>
      )}
    </AdminLayout>
  );
}
